import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, onUnmounted, Ref, ref, watch } from "vue";
  import router from "@/router";
  import store from "@/store";
  import CalculatorAboutYouSection from "@/components/builder/borrow-calculator/CalculatorAboutYouSection.vue";
  import CalculatorYourIncomeSection from "@/components/builder/borrow-calculator/CalculatorIncomeSection.vue";
  import CalculatorExpensesSection from "@/components/builder/borrow-calculator/CalculatorExpensesSection.vue";
  import CalculatorResultSection from "@/components/builder/borrow-calculator/CalculatorResultSection.vue";
  import FilterHouses from "@/components/builder/budget-houses/FilterHouses.vue";
  import ListHouses from "@/components/builder/budget-houses/ListHouses.vue";
  import {
    borrowCalculatorDefault,
    BorrowCalculatorInterface,
    SectionTabs,
    calculatorOptionsDefault,
    CalculatorOptions,
    BorrowCalculatorResult,
  } from "@/types/BorrowCalculator";
  import { POSITION, useToast } from "vue-toastification";
  import {
    changePrimaryColor,
    resetPrimaryColor,
  } from "@/helpers/customer/changePrimaryColor";
  import { HouseDesignInterface } from "@/types/HouseDesignInterface";
  import { BuilderQuote } from "@/types/BuilderQuote";
  import { HouseFiltersFields } from "@/types/HouseFilterFieldsInterface";
  import { BuildBudgetPriceEnum } from "@/enums/BuilderBuildBudgetEnum";

  const noteText =
    "Note: The information provided by the calculator is intended to provide illustrative examples based on stated assumptions and your inputs. Calculations are meant as estimates only and it is advised that you consult with a mortgage broker about your specific circumstances.";
  
export default /*@__PURE__*/_defineComponent({
  __name: 'BuilderBorrowCalculator',
  setup(__props, { expose: __expose }) {
  __expose();

  const estateId = router.currentRoute.value.params.estateId;
  const quoteId = router.currentRoute.value.params.quoteId;
  const estateInfo = ref({});
  const quoteInfo = ref<BuilderQuote>();
  const loading = ref(true);
  const activeTab = ref<SectionTabs>(SectionTabs.ABOUT);
  const isLastTab = computed(() => {
    const sections = Object.values(SectionTabs);

    return activeTab.value === sections[sections.length - 1];
  });
  const form = ref<BorrowCalculatorInterface>({ ...borrowCalculatorDefault });
  const formCalculatorOptions = ref<CalculatorOptions>({
    ...calculatorOptionsDefault,
  });
  const toast = useToast();
  const continueText = ref("Continue");
  const resultCalculator = ref<BorrowCalculatorResult>();
  const houses = ref<HouseDesignInterface[]>();
  const initialBudgetValue = ref<BuildBudgetPriceEnum>(
    BuildBudgetPriceEnum.BUDGET_THRESHOLD_ANY
  );
  const filterFields = ref<HouseFiltersFields>({
    budget: initialBudgetValue.value,
    bedrooms: "Any",
    bathrooms: "Any",
    storeys: "Any",
    lotId: quoteInfo.value ? quoteInfo.value?.selections?.lot?.lotId : "",
  });
  const updateActiveTab = (value: SectionTabs) => {
    activeTab.value = value;
  };

  const updateForm = (field: string, value: string) => {
    if (Object.keys(form.value).includes(field)) {
      form.value[field] = value;
    }
  };

  const updateFormCalculatorOptions = (
    field: string,
    value: "1" | "2" | boolean
  ) => {
    if (Object.keys(formCalculatorOptions.value).includes(field)) {
      formCalculatorOptions.value[field] = value;
    }
  };

  const continueTab = () => {
    const sections = Object.values(SectionTabs);
    let sectionIndex = getSectionIndex();

    if (
      activeTab.value === SectionTabs.INCOME &&
      form.value.JointIncome === "1" &&
      formCalculatorOptions.value.isApplicant1Active
    ) {
      formCalculatorOptions.value.isApplicant1Active = false;
    } else if (sections.length !== sectionIndex) {
      activeTab.value = sections[sectionIndex];
    }
  };

  const backTab = () => {
    const sections = Object.values(SectionTabs);
    let sectionIndex = getSectionIndex();

    if (
      activeTab.value === SectionTabs.INCOME &&
      form.value.JointIncome === "1" &&
      !formCalculatorOptions.value.isApplicant1Active
    ) {
      formCalculatorOptions.value.isApplicant1Active = true;
    } else {
      activeTab.value = sections[sectionIndex - 2];
    }
  };

  const getSectionIndex = () => {
    const sections = Object.values(SectionTabs);
    let sectionIndex = 0;

    sections.forEach((section, index) => {
      if (section === activeTab.value) {
        sectionIndex = index + 1;
      }
    });
    return sectionIndex;
  };

  watch(
    () => activeTab.value,
    () => {
      const sections = Object.values(SectionTabs);
      let sectionIndex = getSectionIndex();

      if (sectionIndex === sections.length - 1) {
        continueText.value = "Calculate";
      } else {
        continueText.value = "Continue";
      }
    }
  );

  const getEstate = () => {
    store
      .dispatch("estate/getEstate", estateId.toString())
      .then((res) => {
        estateInfo.value = res.data;
        changePrimaryColor(estateInfo.value.iconColor);
      })
      .catch(() => {
        toast.error("Something went wrong", {
          position: POSITION.TOP_LEFT,
        });
        resetPrimaryColor();
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const getQuote = () => {
    store
      .dispatch("builder/getQuote", quoteId)
      .then((res) => {
        quoteInfo.value = res.data;
      })
      .catch(() => {
        toast.error("Something went wrong", {
          position: POSITION.TOP_LEFT,
        });
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const handleResult = (result: BorrowCalculatorResult) => {
    resultCalculator.value = result;
  };

  const housesByFilter = (housesData: HouseDesignInterface[]) => {
    houses.value = housesData;
  };

  watch(resultCalculator, () => {
    getInitialBudgetValue();
    store.commit("borrowCalculator/updateFilter", {
      budget: filterFields.value.budget,
    });
  });

  const getInitialBudgetValue = () => {
    if (resultCalculator.value && resultCalculator.value.LoanAmountString) {
      const loanAmount = parseFloat(
        resultCalculator.value.LoanAmountString.replace(/[$,]/g, "")
      );
      const sixtyPercent = Math.round((loanAmount * 0.6) / 5000) * 5000;
      const lowerLimit = parseFloat(
        sixtyPercent.toString().replace(/[$,]/g, "")
      );

      switch (true) {
        case lowerLimit <= 200000:
          filterFields.value.budget =
            BuildBudgetPriceEnum.BUDGET_THRESHOLD_200K;
          break;
        case lowerLimit <= 300000:
          filterFields.value.budget =
            BuildBudgetPriceEnum.BUDGET_THRESHOLD_300K;
          break;
        case lowerLimit <= 400000:
          filterFields.value.budget =
            BuildBudgetPriceEnum.BUDGET_THRESHOLD_400K;
          break;
        case lowerLimit <= 500000:
          filterFields.value.budget =
            BuildBudgetPriceEnum.BUDGET_THRESHOLD_500K;
          break;
        default:
          filterFields.value.budget = BuildBudgetPriceEnum.BUDGET_THRESHOLD_ANY;
          break;
      }
    }
  };

  onMounted(() => {
    getEstate();
    getQuote();
    getInitialBudgetValue();
    store.commit("borrowCalculator/updateFilter", filterFields.value);
  });

  onUnmounted(() => {
    resetPrimaryColor();
  });

const __returned__ = { estateId, quoteId, estateInfo, quoteInfo, loading, activeTab, isLastTab, form, formCalculatorOptions, toast, continueText, noteText, resultCalculator, houses, initialBudgetValue, filterFields, updateActiveTab, updateForm, updateFormCalculatorOptions, continueTab, backTab, getSectionIndex, getEstate, getQuote, handleResult, housesByFilter, getInitialBudgetValue, computed, onMounted, onUnmounted, Ref, ref, watch, get router() { return router }, get store() { return store }, CalculatorAboutYouSection, CalculatorYourIncomeSection, CalculatorExpensesSection, CalculatorResultSection, FilterHouses, ListHouses, get borrowCalculatorDefault() { return borrowCalculatorDefault }, get BorrowCalculatorInterface() { return BorrowCalculatorInterface }, get SectionTabs() { return SectionTabs }, get calculatorOptionsDefault() { return calculatorOptionsDefault }, get CalculatorOptions() { return CalculatorOptions }, get BorrowCalculatorResult() { return BorrowCalculatorResult }, get POSITION() { return POSITION }, get useToast() { return useToast }, get changePrimaryColor() { return changePrimaryColor }, get resetPrimaryColor() { return resetPrimaryColor }, get HouseDesignInterface() { return HouseDesignInterface }, get BuilderQuote() { return BuilderQuote }, get HouseFiltersFields() { return HouseFiltersFields }, get BuildBudgetPriceEnum() { return BuildBudgetPriceEnum } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})